/**
 * Token storage module - manages authentication tokens with no external dependencies.
 * This module breaks the circular dependency between helpers.js and Auth.js by
 * providing pure token storage operations and using dependency injection for API calls.
 */
import useAuthStore from "../stores/useAuthStore";
import useErrorStore from "../stores/useErrorStore";

// Callback for logout side effects (e.g., updating user status)
let onLogoutCallback = null;

/**
 * Register a callback to be called during logout.
 * Used by Auth.js to register userService.updateUserStatus without creating circular deps.
 * @param {Function} callback - async function(userId) called before clearing credentials
 */
export function registerLogoutCallback(callback) {
  onLogoutCallback = callback;
}

/**
 * Get the stored refresh token.
 */
export function getRefreshToken() {
  return useAuthStore.getState().refreshToken;
}

/**
 * Get the current user auth data.
 */
export function getUserAuth() {
  return useAuthStore.getState().userAuth;
}

/**
 * Update the access token (JWT) after a refresh.
 */
export function setAccessToken(token) {
  useAuthStore.getState().updateUserAuth({ jwt: token });
}

/**
 * Update the refresh token (for token rotation).
 */
export function setRefreshToken(token) {
  if (token) {
    useAuthStore.getState().setRefreshToken(token);
  }
}

/**
 * Clear the refresh token from storage.
 */
export function clearRefreshToken() {
  useAuthStore.getState().clearRefreshToken();
}

/**
 * Clear all authentication data.
 * Calls registered logout callback if set.
 */
export async function clearCredentials() {
  const user = useAuthStore.getState().userAuth;

  // Call logout callback (e.g., update user status) before clearing
  if (onLogoutCallback && user) {
    try {
      await onLogoutCallback(user.id);
    } catch (e) {
      console.warn("Logout callback failed:", e);
    }
  }

  useAuthStore.getState().clearAuth();
  localStorage.removeItem("userStatus");
  useErrorStore.getState().clearError();
}

/**
 * Refresh access token using the provided request function.
 * @param {Function} doRequest - The request function to call (doRequestWithoutAuth)
 * @returns {Promise<Object>} The new credentials
 */
export async function refreshAccessToken(doRequest) {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const credentials = await doRequest("POST", "/auth/refresh", {
    data: { refresh_token: refreshToken },
  });

  // Update stored tokens
  setAccessToken(credentials.token);
  if (credentials.refresh_token) {
    setRefreshToken(credentials.refresh_token);
  }

  window.dispatchEvent(new Event("tokenRefreshed"));

  return credentials;
}

/**
 * Revoke refresh token on server (best-effort).
 * @param {Function} doRequest - The request function to call (doRequestWithoutAuth)
 */
export async function revokeRefreshToken(doRequest) {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (refreshToken) {
    try {
      await doRequest("POST", "/auth/logout", {
        data: { refresh_token: refreshToken },
      });
    } catch (e) {
      // Ignore errors - token may already be invalid
      console.warn("Failed to revoke refresh token:", e);
    }
  }
}
